import type { EditPriceListRequest, PurchasePriceList, QualityAcceptanceProperty } from '@/types/buy';

// Чистая логика формы прайс-листа на покупку: черновик цен, счётчики, заполнение цен со 100 % предоплатой и перечень
// того, что мешает отправке. Года у закупки нет (решение 2026-11-04): прайс-лист определяется базисом и культурой.

import { PRICE_MAX, PRICE_MIN } from '@/lib/priceLimits';

export { PRICE_MAX, PRICE_MIN };

/** Цена в поле: число или пусто. */
export type PriceCell = number | '';
export type PriceColumn = 'p0' | 'p100';
export const PRICE_COLUMNS: readonly PriceColumn[] = ['p0', 'p100'];

export interface PriceDraftRow {
  readonly p0: PriceCell;
  readonly p100: PriceCell;
}
export type PriceDraft = Readonly<Record<string, PriceDraftRow>>;

export const EMPTY_ROW: PriceDraftRow = { p0: '', p100: '' };

export const rowKey = (goodsType: string, propsSetId: number): string => `${goodsType}|${propsSetId}`;

/** Цена допустима: задана и в платформенном диапазоне. */
export const isValidPrice = (value: PriceCell): boolean => value !== '' && value >= PRICE_MIN && value <= PRICE_MAX;

/** Цена задана, но вне диапазона: ошибка видна сразу, форма введённое не подменяет. */
export const isOutOfRange = (value: PriceCell): boolean => value !== '' && !isValidPrice(value);

/** Значение `NumberInput` («» или число) в ячейку черновика. */
export const toCell = (value: number | string): PriceCell => {
  if (value === '') {
    return '';
  }
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : '';
};

/** Черновик цен из строк качества шаблона: сохранённые цены прайс-листа или пусто. */
export function draftFromTemplate(list: PurchasePriceList): PriceDraft {
  const draft: Record<string, PriceDraftRow> = {};
  for (const item of list.goodsList) {
    for (const row of item.qualityRows) {
      const prices = row.pricesByPrepaymentMap;
      draft[rowKey(item.goodsType, row.propsSetId)] = { p0: prices?.['0'] ?? '', p100: prices?.['100'] ?? '' };
    }
  }
  return draft;
}

/** Ключи строк в порядке на экране: по ним сводка ведёт к первой цене вне диапазона. */
export function rowKeysInOrder(list: PurchasePriceList): string[] {
  return list.goodsList.flatMap((item) => item.qualityRows.map((row) => rowKey(item.goodsType, row.propsSetId)));
}

export interface PublishStats {
  /** Строк, которые будут опубликованы: хотя бы одна допустимая цена. */
  readonly filled: number;
  /** Цен вне диапазона: мешают отправке. */
  readonly invalid: number;
  readonly total: number;
}

export function publishStats(draft: PriceDraft): PublishStats {
  const rows = Object.values(draft);
  return {
    filled: rows.filter((row) => isValidPrice(row.p0) || isValidPrice(row.p100)).length,
    invalid: rows.reduce((count, row) => count + (isOutOfRange(row.p0) ? 1 : 0) + (isOutOfRange(row.p100) ? 1 : 0), 0),
    total: rows.length,
  };
}

/** Первое поле с ценой вне диапазона в порядке на экране; null — таких нет. */
export function firstOutOfRange(draft: PriceDraft, order: readonly string[]): { key: string; column: PriceColumn } | null {
  for (const key of order) {
    const row = draft[key];
    if (!row) {
      continue;
    }
    for (const column of PRICE_COLUMNS) {
      if (isOutOfRange(row[column])) {
        return { key, column };
      }
    }
  }
  return null;
}

// ── Цена со 100 % предоплатой = цена без предоплаты − шаг ─────────────────────────

/**
 * Наибольший шаг, при котором ни одна цена со 100 % предоплатой не опустится ниже минимума; null — нет ни одной
 * допустимой цены без предоплаты, заполнять нечем.
 */
export function maxShift(draft: PriceDraft): number | null {
  const bases = Object.values(draft)
    .map((row) => row.p0)
    .filter((value): value is number => isValidPrice(value));
  return bases.length === 0 ? null : Math.min(...bases) - PRICE_MIN;
}

export interface ShiftPreview {
  /** Сколько цен со 100 % предоплатой будет заполнено. */
  readonly fill: number;
  /** Из них уже введённых — будут заменены. */
  readonly replace: number;
}

export function shiftPreview(draft: PriceDraft): ShiftPreview {
  const rows = Object.values(draft).filter((row) => isValidPrice(row.p0));
  return { fill: rows.length, replace: rows.filter((row) => row.p100 !== '').length };
}

/** Допустим ли шаг: целое от 1 до наибольшего шага. */
export function isShiftValid(step: PriceCell, max: number | null): step is number {
  return step !== '' && Number.isInteger(step) && step >= 1 && max != null && step <= max;
}

/** Заполнить цены со 100 % предоплатой у всех строк с допустимой ценой без предоплаты. */
export function applyShift(draft: PriceDraft, step: number): PriceDraft {
  const next: Record<string, PriceDraftRow> = {};
  for (const [key, row] of Object.entries(draft)) {
    next[key] = isValidPrice(row.p0) ? { ...row, p100: (row.p0 as number) - step } : row;
  }
  return next;
}

// ── Что мешает отправке ─────────────────────────────────────────────────────────

/** Раздел формы: по нему строится строка «Осталось заполнить» и порядок сводки. */
export type PriceListSection = 'where' | 'prices' | 'acceptance' | 'consignee' | 'agree';
export const SECTION_ORDER: readonly PriceListSection[] = ['where', 'prices', 'acceptance', 'consignee', 'agree'];

/** Что мешает отправке; `target` — элемент, к которому ведёт сводка. */
export type PriceListBlocker =
  | 'address'
  | 'goods'
  | 'needPrice'
  | 'outOfRange'
  | 'credit'
  | 'qa'
  | 'consignee'
  | 'agree';

export const BLOCKER_SECTION: Record<PriceListBlocker, PriceListSection> = {
  address: 'where',
  goods: 'where',
  needPrice: 'prices',
  outOfRange: 'prices',
  credit: 'acceptance',
  qa: 'acceptance',
  consignee: 'consignee',
  agree: 'agree',
};

export interface BlockerInput {
  readonly hasAddress: boolean;
  readonly hasGoods: boolean;
  readonly stats: PublishStats;
  readonly hasCredit: boolean;
  readonly qaComplete: boolean;
  readonly consigneeChosen: boolean;
  readonly agree: boolean;
}

/** Всё, что мешает отправке, в порядке разделов на экране; пусто — форму можно отправить. */
export function priceListBlockers(input: BlockerInput): PriceListBlocker[] {
  const list: PriceListBlocker[] = [];
  if (!input.hasAddress) {
    list.push('address');
  }
  if (!input.hasGoods) {
    list.push('goods');
  }
  if (input.hasAddress && input.hasGoods) {
    if (input.stats.filled === 0) {
      list.push('needPrice');
    }
    if (input.stats.invalid > 0) {
      list.push('outOfRange');
    }
  }
  if (!input.hasCredit) {
    list.push('credit');
  }
  if (!input.qaComplete) {
    list.push('qa');
  }
  if (!input.consigneeChosen) {
    list.push('consignee');
  }
  if (!input.agree) {
    list.push('agree');
  }
  return list;
}

/**
 * Часть идентификатора из произвольной строки (культура с классом, название показателя): без пробелов и знаков,
 * иначе `aria-describedby` поля распался бы на несколько несуществующих идентификаторов и ошибку не объявили бы.
 */
export function idPart(value: string): string {
  return encodeURIComponent(value).replace(/[^A-Za-z0-9_-]/g, '_');
}

/** Идентификатор элемента формы: по нему сводка ведёт к полю. */
export function priceListFieldId(target: string): string {
  return `price-list-${target}`;
}

/** Идентификатор поля цены строки качества. */
export function priceCellId(key: string, column: PriceColumn): string {
  return priceListFieldId(`price-${column}-${idPart(key)}`);
}

/** Идентификатор поля правила приёмки «не по ГОСТу»: порог (`inc`) или снижение веса (`dec`). */
export function qaFieldId(name: string, field: 'inc' | 'dec'): string {
  return priceListFieldId(`qa-${idPart(name)}-${field}`);
}

// ── Запрос сохранения ────────────────────────────────────────────────────────────

export interface PayloadInput {
  readonly template: PurchasePriceList;
  readonly draft: PriceDraft;
  readonly basisId?: number;
  readonly agree: boolean;
  readonly creditType: string;
  readonly byGost: boolean;
  readonly qaProperties?: QualityAcceptanceProperty[];
  readonly fileIds: number[];
  readonly comment: string;
  readonly consigneeInn?: string;
}

/** Тело сохранения прайс-листа: строки качества шаблона с ценами черновика, без года урожая. */
export function buildPriceListPayload(input: PayloadInput): EditPriceListRequest {
  const { template, draft } = input;
  return {
    address: template.address,
    addressTitle: template.addressTitle,
    basisId: template.basisId ?? input.basisId,
    agreeOffer: input.agree,
    qualityCreditType: input.creditType,
    qualityAcceptanceByGost: input.byGost,
    qualityAcceptanceProperties: input.qaProperties,
    specialTermsFileIds: input.fileIds,
    userComment: input.comment.trim() || undefined,
    consigneeInn: input.consigneeInn,
    goodsList: template.goodsList.map((item) => ({
      goodsType: item.goodsType,
      parentGoodsType: item.parentGoodsType,
      goodsClass: item.goodsClass,
      qualityRows: item.qualityRows.map((row) => {
        const cell = draft[rowKey(item.goodsType, row.propsSetId)] ?? EMPTY_ROW;
        return {
          ...row,
          pricesByPrepaymentMap: { '0': cell.p0 === '' ? null : cell.p0, '100': cell.p100 === '' ? null : cell.p100 },
        };
      }),
    })),
  };
}
